// src/utils/exportUtils.js
// CSV export utilities for outreach logs and payment history

/**
 * Convert an array of objects to a CSV string.
 * Handles commas and quotes inside values.
 */
function toCSV(rows, columns) {
  const header = columns.map(c => JSON.stringify(c.label)).join(',');
  const body = rows.map(row =>
    columns.map(c => {
      const val = c.get ? c.get(row) : (row[c.key] ?? '');
      return JSON.stringify(String(val));
    }).join(',')
  ).join('\n');
  return header + '\n' + body;
}

/**
 * Trigger a browser download of a CSV string.
 */
function downloadCSV(csvString, filename) {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Export a single tenant's outreach log as CSV.
 */
export function exportOutreachLog(tenant) {
  const log = tenant.outreachLog || [];
  if (!log.length) { alert('No outreach log entries to export.'); return; }

  const columns = [
    { label: 'Date',     key: 'date' },
    { label: 'Channel',  key: 'channel' },
    { label: 'Status',   key: 'status' },
    { label: 'Template', key: 'templateUsed' },
    { label: 'Phone',    key: 'phone' },
    { label: 'Message',  key: 'message' },
  ];

  const csv = toCSV(log, columns);
  const safe = (tenant.name || 'tenant').replace(/[^a-z0-9]/gi, '_').toLowerCase();
  downloadCSV(csv, `tenafi_outreach_${safe}_${new Date().toISOString().slice(0,10)}.csv`);
}

/**
 * Export ALL outreach logs across all tenants in a property.
 */
export function exportAllOutreach(tenants, propertyName) {
  const rows = [];
  tenants.forEach(tenant => {
    (tenant.outreachLog || []).forEach(entry => {
      rows.push({
        tenant_name:   tenant.name || '',
        unit:          tenant.unit || '',
        risk_tier:     tenant.risk?.tier || '',
        risk_score:    tenant.risk?.score ?? '',
        balance:       tenant.currentBalance ?? '',
        date:          entry.date || '',
        channel:       entry.channel || '',
        status:        entry.status || '',
        template_used: entry.templateUsed || '',
        phone:         entry.phone || '',
        message:       entry.message || '',
      });
    });
  });

  if (!rows.length) { alert('No outreach history to export.'); return; }

  const columns = [
    { label: 'Tenant Name',   key: 'tenant_name' },
    { label: 'Unit',          key: 'unit' },
    { label: 'Risk Tier',     key: 'risk_tier' },
    { label: 'Risk Score',    key: 'risk_score' },
    { label: 'Balance',       key: 'balance' },
    { label: 'Date',          key: 'date' },
    { label: 'Channel',       key: 'channel' },
    { label: 'Status',        key: 'status' },
    { label: 'Template Used', key: 'template_used' },
    { label: 'Phone',         key: 'phone' },
    { label: 'Message',       key: 'message' },
  ];

  const csv = toCSV(rows, columns);
  const safe = (propertyName || 'property').replace(/[^a-z0-9]/gi, '_').toLowerCase();
  downloadCSV(csv, `tenafi_outreach_${safe}_${new Date().toISOString().slice(0,10)}.csv`);
}

/**
 * Export a single tenant's payment history as CSV.
 */
export function exportPaymentHistory(tenant) {
  const history = tenant.paymentHistory || [];
  if (!history.length) { alert('No payment history to export.'); return; }

  const columns = [
    { label: 'Date',        key: 'date' },
    { label: 'Amount Paid', key: 'amount' },
    { label: 'Days Late',   key: 'daysLate' },
    { label: 'Status', get: row => {
      if (!row.amount) return 'Missed';
      if (row.daysLate > 10) return 'Very Late';
      if (row.daysLate > 3)  return 'Late';
      return 'On Time';
    }},
  ];

  const csv = toCSV(history, columns);
  const safe = (tenant.name || 'tenant').replace(/[^a-z0-9]/gi, '_').toLowerCase();
  downloadCSV(csv, `tenafi_payments_${safe}_${new Date().toISOString().slice(0,10)}.csv`);
}
